/**
 * Core platform detection functions
 */

import type { NativePlatform, TelegramPlatform, CapacitorInfo, TelegramInfo } from './types';

/**
 * Detect if running as PWA (installed)
 */
export function isPWA(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  // Check if running in standalone mode (iOS)
  if ('standalone' in window.navigator && (window.navigator as any).standalone) {
    return true;
  }

  // Check display mode (Android and desktop)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }

  // Check display mode fullscreen
  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    return true;
  }

  // Check display mode minimal-ui
  if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    return true;
  }

  return false;
}

/**
 * Check if we should load Telegram SDK based on URL hints
 * Uses strict checks to avoid false positives in regular browsers
 */
export function shouldLoadTelegramSDK(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  // Check URL parameters for Telegram indicators (strict check)
  const urlParams = new URLSearchParams(window.location.search);
  const hasTelegramParams =
    urlParams.has('tgWebAppData') ||
    urlParams.has('tgWebAppVersion') ||
    urlParams.has('tgWebAppPlatform');

  if (hasTelegramParams) {
    return true;
  }

  // Check referrer - must be from t.me domain
  if (document.referrer && document.referrer.includes('t.me')) {
    return true;
  }

  // Check hash for Telegram params (strict check)
  const hash = window.location.hash;
  if (hash && (hash.includes('tgWebAppData=') || hash.includes('tgWebAppVersion='))) {
    return true;
  }

  // Check hostname - only load for tg.legere.app domain
  if (window.location.hostname === 'tg.legere.app') {
    return true;
  }

  return false;
}

/**
 * Detect if running in Telegram Mini App
 * Checks if Telegram WebApp SDK is loaded and initialized with actual Telegram data
 */
export function isTelegramMiniApp(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const webApp = (window as any).Telegram?.WebApp;

  // WebApp must exist and have a version
  if (!webApp || !webApp.version) {
    return false;
  }

  // CRITICAL: Check if we have actual Telegram init data
  const hasInitData = !!(webApp.initData || (webApp.initDataUnsafe && Object.keys(webApp.initDataUnsafe).length > 0));

  // Check for platform
  const hasPlatform = !!(webApp.platform) && webApp.platform !== 'unknown';

  // Check for colorScheme
  const hasColorScheme = !!(webApp.colorScheme);

  // Must have ALL these indicators
  return hasInitData && hasPlatform && hasColorScheme;
}

/**
 * Detect if running in Capacitor
 */
export function isCapacitor(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  // Check for Capacitor object
  if ((window as any).Capacitor) {
    return true;
  }

  // Check for Capacitor native bridge
  if ((window as any).CapacitorWebView) {
    return true;
  }

  return false;
}

/**
 * Detect if mobile device
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent.toLowerCase();

  // Check user agent
  const mobileKeywords = [
    'android',
    'webos',
    'iphone',
    'ipad',
    'ipod',
    'blackberry',
    'windows phone',
    'mobile',
  ];

  const isMobileUA = mobileKeywords.some((keyword) => userAgent.includes(keyword));

  // Check touch support and screen size
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;

  return isMobileUA || (hasTouchScreen && isSmallScreen);
}

/**
 * Get platform name
 */
export function getPlatform(): string {
  if (typeof navigator === 'undefined') {
    return 'unknown';
  }

  // Modern API
  if ('userAgentData' in navigator && (navigator as any).userAgentData) {
    const platform = (navigator as any).userAgentData.platform;
    if (platform) return platform;
  }

  // Fallback to legacy API
  if ('platform' in navigator) {
    return (navigator as any).platform || 'unknown';
  }

  return 'unknown';
}

/**
 * Get Telegram WebApp instance if available
 */
export function getTelegramWebApp() {
  if (typeof window === 'undefined') {
    return null;
  }
  return (window as any).Telegram?.WebApp || null;
}

/**
 * Get Capacitor instance if available
 */
export function getCapacitor() {
  if (typeof window === 'undefined') {
    return null;
  }
  return (window as any).Capacitor || null;
}

/**
 * Get detailed Capacitor information using native API
 */
export function getCapacitorInfo(): CapacitorInfo | null {
  const Capacitor = getCapacitor();
  if (!Capacitor) {
    return null;
  }

  const isNativePlatform = Capacitor.isNativePlatform ? Capacitor.isNativePlatform() : false;
  const platformName = Capacitor.getPlatform ? Capacitor.getPlatform() : 'unknown';

  let platform: NativePlatform = 'unknown';
  if (platformName === 'ios') platform = 'ios';
  else if (platformName === 'android') platform = 'android';
  else if (platformName === 'web') platform = 'web';

  return {
    isNativePlatform,
    platform,
    isPluginAvailable: (plugin: string) => {
      return Capacitor.isPluginAvailable ? Capacitor.isPluginAvailable(plugin) : false;
    },
    nativeVersion: Capacitor.nativeVersion,
  };
}

/**
 * Get detailed Telegram information using native SDK API
 */
export function getTelegramInfo(): TelegramInfo | null {
  const webApp = getTelegramWebApp();
  if (!webApp) {
    return null;
  }

  return {
    platform: (webApp.platform || 'unknown') as TelegramPlatform,
    version: webApp.version || '0.0',
    colorScheme: webApp.colorScheme || 'dark',
    viewportHeight: webApp.viewportHeight || window.innerHeight,
    viewportStableHeight: webApp.viewportStableHeight || window.innerHeight,
    isExpanded: webApp.isExpanded || false,
    isClosingConfirmationEnabled: webApp.isClosingConfirmationEnabled || false,
    headerColor: webApp.headerColor || '#000000',
    backgroundColor: webApp.backgroundColor || '#ffffff',
    safeAreaInsetTop: webApp.safeAreaInset?.top || 0,
    safeAreaInsetBottom: webApp.safeAreaInset?.bottom || 0,
    contentSafeAreaInsetTop: webApp.contentSafeAreaInset?.top || 0,
    contentSafeAreaInsetBottom: webApp.contentSafeAreaInset?.bottom || 0,
  };
}

/**
 * Get Telegram WebApp init data
 */
export function getTelegramInitData() {
  const webApp = getTelegramWebApp();
  if (!webApp) {
    return null;
  }

  return {
    initData: webApp.initData || '',
    initDataUnsafe: webApp.initDataUnsafe || {},
    version: webApp.version || '',
    platform: webApp.platform || '',
    colorScheme: webApp.colorScheme || 'dark',
    user: webApp.initDataUnsafe?.user || null,
  };
}

/**
 * Detect native platform with priority:
 * 1. Capacitor native platform
 * 2. Telegram platform
 * 3. Fallback detection from user agent
 */
export function detectNativePlatform(
  capacitorInfo: CapacitorInfo | null,
  telegramInfo: TelegramInfo | null,
  userAgent: string
): { platform: NativePlatform; isNative: boolean } {
  if (capacitorInfo) {
    return {
      platform: capacitorInfo.platform,
      isNative: capacitorInfo.isNativePlatform,
    };
  }

  if (telegramInfo) {
    const tgPlatform = telegramInfo.platform;
    if (tgPlatform === 'ios') {
      return { platform: 'ios', isNative: true };
    } else if (tgPlatform === 'android' || tgPlatform === 'android_x') {
      return { platform: 'android', isNative: true };
    } else {
      return { platform: 'web', isNative: false };
    }
  }

  // Fallback to user agent
  const ua = userAgent.toLowerCase();
  if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod') || ua.includes('mac')) {
    return { platform: 'ios', isNative: false };
  } else if (ua.includes('android')) {
    return { platform: 'android', isNative: false };
  }

  return { platform: 'web', isNative: false };
}
